/* eslint-disable react-refresh/only-export-components */
/**
 * ConversationStateProvider
 *
 * Contexte dédié pour gérer l'état des conversations (Business Logic)
 * Extrait de ConversationProvider pour éviter re-renders inutiles
 *
 * Responsabilités :
 * - État conversation (ID, messages)
 * - Persistence dans localStorage
 * - Synchronisation avec l'historique
 *
 * @see Docs/Architecture-ConversationProvider.md
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { useSafeLocation } from "@core/hooks/useSafeLocation";
import { ErrorFactory, logError } from "@core/lib/error-handling";
import type { Message } from "@core/services/ConversationService";

// Re-export pour faciliter l'import
export type { Message };

export interface ConversationStateContextType {
  // État conversation
  conversationId: string | null;
  messages: Message[];

  // Actions
  setConversationId: (id: string | null) => void;
  addMessage: (message: Omit<Message, "id" | "timestamp">) => void;
  setMessages: (messages: Message[] | ((prev: Message[]) => Message[])) => void;
  clearMessages: () => void;
}

const ConversationStateContext = createContext<ConversationStateContextType | undefined>(undefined);

const STORAGE_KEY = "prototype_messages";

/**
 * Transient connection-status messages should never be persisted: they reflect
 * a moment in time, not real conversation. If they leak into localStorage they
 * resurrect on every reload and look like a permanent outage to the user
 * (cf. the 2026-04-30 hyper-task incident — every guest who hit the broken
 * window kept seeing "temporairement indisponible" forever after).
 *
 * This filter runs both on load (migration for affected users) and on save
 * (prevents future pollution).
 */
function isTransientConnectionMessage(msg: { id?: string; content?: string }): boolean {
  if (typeof msg.id === "string") {
    if (
      msg.id.startsWith("connection-error-") ||
      msg.id.startsWith("reconnected-") ||
      msg.id.startsWith("guest-mode-")
    ) {
      return true;
    }
  }
  if (typeof msg.content === "string") {
    if (
      msg.content.includes("temporairement indisponible") ||
      msg.content.includes("Je suis de nouveau disponible")
    ) {
      return true;
    }
  }
  return false;
}

interface ConversationStateProviderProps {
  children: ReactNode;
}

export function ConversationStateProvider({ children }: ConversationStateProviderProps) {
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);

  const location = useSafeLocation();

  // Charger les messages depuis localStorage au démarrage
  useEffect(() => {
    // 🛡️ PROTECTION ULTIME: Si on est sur une nouvelle création (?new=), on ignore le localStorage
    // Cela empêche la "résurrection" des vieux messages
    const urlParams = new URLSearchParams(location.search);
    let shouldLoad = true;

    if (urlParams.has("new")) {
      setMessages([]);
      localStorage.removeItem(STORAGE_KEY);
      shouldLoad = false;
    }

    if (shouldLoad) {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored) as Array<{
            id: string;
            content: string;
            isAI: boolean;
            timestamp: string | Date;
          }>;
          // Strip any transient connection-status messages that leaked into
          // storage from the 2026-04-30 incident (or any future one).
          const cleaned = parsed.filter((msg) => !isTransientConnectionMessage(msg));
          if (cleaned.length !== parsed.length) {
            // Persist the cleaned version so we don't filter on every reload.
            try {
              localStorage.setItem(STORAGE_KEY, JSON.stringify(cleaned));
            } catch {
              // Non-fatal — re-cleaning on next load is fine.
            }
          }
          setMessages(
            cleaned.map((msg) => ({
              conversationId: "",
              role: msg.isAI ? "assistant" : "user",
              timestamp: new Date(msg.timestamp),
              id: msg.id,
              content: msg.content,
              isAI: msg.isAI,
            }))
          );
        }
      } catch (error) {
        logError(
          ErrorFactory.storage(
            "Failed to load messages from localStorage",
            "Impossible de charger l'historique de conversation",
          ),
          {
            component: "ConversationStateProvider",
            operation: "loadMessages",
            metadata: { error },
          },
        );
      }
    }

    // Écouter les événements de reset
    const handleChatReset = (event: CustomEvent) => {
      const strategy = event.detail;
      if (strategy.resetType === "full") {
        setMessages([]);
        localStorage.removeItem(STORAGE_KEY);
      }
    };

    window.addEventListener("chat-reset", handleChatReset as EventListener);
    return () => {
      window.removeEventListener("chat-reset", handleChatReset as EventListener);
    };
  }, [location.search]);

  // Sauvegarder les messages dans localStorage à chaque changement
  useEffect(() => {
    try {
      // Never persist transient connection-status messages — they should
      // disappear on reload, not haunt the conversation forever.
      const persistable = messages.filter((m) => !isTransientConnectionMessage(m));
      localStorage.setItem(STORAGE_KEY, JSON.stringify(persistable));
    } catch (error) {
      logError(
        ErrorFactory.storage(
          "Failed to save messages to localStorage",
          "Impossible de sauvegarder l'historique de conversation",
        ),
        { component: "ConversationStateProvider", operation: "saveMessages", metadata: { error } },
      );
    }
  }, [messages]);

  // Ajouter un message
  const addMessage = useCallback((message: Omit<Message, "id" | "timestamp">) => {
    const newMessage: Message = {
      ...message,
      id: `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, newMessage]);
  }, []);

  // Effacer tous les messages
  const clearMessages = useCallback(() => {
    setMessages([]);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const value: ConversationStateContextType = {
    conversationId,
    messages,
    setConversationId,
    addMessage,
    setMessages,
    clearMessages,
  };

  return (
    <ConversationStateContext.Provider value={value}>{children}</ConversationStateContext.Provider>
  );
}

/**
 * Hook pour accéder à l'état conversation
 *
 * @throws Error si utilisé hors du ConversationStateProvider
 */
export function useConversationState(): ConversationStateContextType {
  const context = useContext(ConversationStateContext);

  // Ajouter un guard pour éviter l'erreur pendant les renders
  if (!context) {
    console.error("🔍 useConversationState called outside ConversationStateProvider - context is null");
    console.error("🔍 Stack trace:", new Error().stack);
    // Retourner un contexte par défaut pour éviter l'erreur
    return {
      conversationId: null,
      messages: [], // ✅ Tableau vide au lieu de null
      setConversationId: () => {
        console.warn("🔍 setConversationId called outside ConversationStateProvider - no-op");
      },
      addMessage: () => {
        console.warn("🔍 addMessage called outside ConversationStateProvider - no-op");
      },
      setMessages: () => {
        console.warn("🔍 setMessages called outside ConversationStateProvider - no-op");
      },
      clearMessages: () => {
        console.warn("🔍 clearMessages called outside ConversationStateProvider - no-op");
      },
    };
  }

  return context;
}

/**
 * Hooks spécialisés pour éviter re-renders inutiles
 */

/**
 * Hook pour accéder uniquement aux messages
 * Le composant ne re-render que si messages change
 */
export function useConversationMessages() {
  const { messages } = useConversationState();
  return messages;
}

/**
 * Hook pour accéder uniquement à l'ID de conversation
 * Le composant ne re-render que si conversationId change
 */
export function useConversationId() {
  const { conversationId } = useConversationState();
  return conversationId;
}

/**
 * Hook pour accéder uniquement aux actions
 * Le composant ne re-render jamais (actions stables)
 */
export function useConversationActions() {
  const { addMessage, setMessages, clearMessages, setConversationId } = useConversationState();
  return { addMessage, setMessages, clearMessages, setConversationId };
}
